// @owners { team: patients }
import { COLORS } from '@alto/design-library-tokens';
import { AltoSpinningLoader, ListPage, Row, Separator, SmPadding, SmSpacing } from '@alto/design-system';
import { useNavigation } from '@alto/navigation';
import React, { type ReactElement, useCallback, useEffect, useRef, useState } from 'react';
import { Platform, RefreshControl } from 'react-native';
import { resetFilters } from '~shared/actions/ui/medList';
import { getImage } from '~shared/assets/images/getImage';
import getHasFamily from '~shared/features/users/selectors/getHasFamily';
import { useAnalytics, useIsFeatureDisabledForCompany } from '~shared/hooks';
import { EVENTS } from '~shared/lib/analytics/src/constants';
import { ORIGIN_NAMES } from '~shared/lib/analytics/src/getOrigin';
import { useDispatchShared, useSelectorShared } from '~shared/store';
import { useAddToCartFlow } from '../../cart';
import { useMedList } from '../queries/useMedList';
import { AddMedicationButton } from './AddMedicationButton';
import { MedListItem } from './MedListItem';
import { FamilyMemberFilterTag, FilterTypeTag, SortTag } from './filter-tags';

const Loader = () => (
  <Row
    centerHorizontally
    backgroundColor={COLORS.BACKGROUND_COLORS.GREY_LIGHTEST}
  >
    <SmPadding>
      <AltoSpinningLoader small={false} />
    </SmPadding>
  </Row>
);

const MIN_MEDS_TO_SHOW_FILTERS = 4;
const MedicationsLoader = () => {
  return (
    <ListPage.Root>
      <ListPage.Header.Root>
        <ListPage.Header.Content.Root>
          <Row
            centerVertically
            spaceBetween
            testID="loading"
          >
            <ListPage.Header.Content.Title>Medications</ListPage.Header.Content.Title>
          </Row>
        </ListPage.Header.Content.Root>
      </ListPage.Header.Root>
      <ListPage.Content.Loader listItemHeight={120} />
    </ListPage.Root>
  );
};

const EmptyState = ({
  areFiltersApplied,
  shouldDisableTransferPrescription,
}: {
  readonly areFiltersApplied: boolean;
  readonly shouldDisableTransferPrescription: boolean;
}) => {
  const dispatch = useDispatchShared();
  const { navigate } = useNavigation();
  const { trackEvent } = useAnalytics();

  const handleResetFilters = useCallback(() => {
    // retain sort and reset the filters for prescription status and family members
    dispatch(resetFilters());
  }, [dispatch]);

  const navigateToAddMedications = useCallback(() => {
    trackEvent({ event: EVENTS.ADD_MED_CLICKED });
    navigate('RouteTransferPrescription', { origin: ORIGIN_NAMES.MED_LIST });
  }, [navigate, trackEvent]);

  // If filters applied, remind to reset the filters
  if (areFiltersApplied) {
    return (
      <ListPage.Content.EmptyState
        ctaText="Reset filters"
        description="Try broadening your filters"
        image={getImage('outOfStock')}
        onPressCTA={handleResetFilters}
        title="No results"
      />
    );
  }

  // If no filter applied and transfer prescription feature is disabled, show no results without reset
  if (shouldDisableTransferPrescription) {
    return (
      <ListPage.Content.EmptyState
        ctaText={undefined}
        description={undefined}
        image={getImage('outOfStock')}
        onPressCTA={undefined}
        title="No results"
      />
    );
  }

  // If no filter applied and transfer prescription is not disabled, show add med for transfer prescription
  return (
    <ListPage.Content.EmptyState
      ctaText="Get started"
      description="Provide your current prescription information and we'll do the rest"
      image={getImage('addMedication')}
      onPressCTA={navigateToAddMedications}
      title="Add a medication"
    />
  );
};

export const MedList = ({
  origin,
  ViewCartComponent,
}: {
  readonly origin?: string;
  readonly ViewCartComponent?: ReactElement;
}) => {
  const [refreshing, setRefreshing] = useState(false);
  const { trackPageView } = useAnalytics();
  const { setOptions } = useNavigation();
  const hasFamily = useSelectorShared(getHasFamily);
  const sortedBy = useSelectorShared((state) => state.persistedUI.medList.sortType);
  const filteredFor = useSelectorShared((state) => state.persistedUI.medList.filters);
  const { isFeatureDisabled: shouldDisableTransferPrescription, isLoading: isFeatureDisablingLoading } =
    useIsFeatureDisabledForCompany('transfer_prescription');

  // setOptions is here to override the header options in TabNavigator.tsx - we don't show a header on this page
  useEffect(() => {
    setOptions({ headerShown: false });
  }, [setOptions]);

  const {
    areFiltersApplied,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isPendingMedications,
    medications = [],
    refetch,
  } = useMedList();

  const { handleAddToCart } = useAddToCartFlow({ origin: ORIGIN_NAMES.MED_LIST });

  /**
   * We only want to show sorting and filters under these conditions:
   * 1. there are applied filters
   *    - we need to be able to update or clear filters if they're applied and the
   *    amount of returned meds is less than 4
   * 2. there are family members
   * 3. there are 4 or more medications
   */
  const shouldShowTags = areFiltersApplied || hasFamily || medications.length >= MIN_MEDS_TO_SHOW_FILTERS;
  const hasTrackedPageView = useRef(false);

  useEffect(() => {
    if (isPendingMedications || hasTrackedPageView.current) return;
    hasTrackedPageView.current = true;
    trackPageView({
      event: EVENTS.MED_LIST_VIEWED,
      params: {
        origin,
        hasSortFeature: shouldShowTags,
        hasFamilyFilterFeature: (areFiltersApplied || medications.length >= MIN_MEDS_TO_SHOW_FILTERS) && hasFamily,
        hasFilterFeature: shouldShowTags,
        sortedBy,
        filteredFor,
      },
    });
  }, [
    isPendingMedications,
    origin,
    shouldShowTags,
    areFiltersApplied,
    hasFamily,
    medications.length,
    sortedBy,
    filteredFor,
    trackPageView,
  ]);

  const onRefresh = useCallback(() => {
    setRefreshing(true);
    setTimeout(() => {
      refetch();
      setRefreshing(false);
    }, 1000);
  }, [refetch]);

  if (isPendingMedications || refreshing || isFeatureDisablingLoading) return <MedicationsLoader />;

  return (
    <ListPage.Root
      backgroundColor={Platform.OS !== 'web' ? COLORS.BACKGROUND_COLORS.GREY_LIGHTEST : COLORS.BACKGROUND_COLORS.WHITE}
    >
      <ListPage.Content.FlatList
        data={medications}
        // eslint-disable-next-line react-native/no-inline-styles
        contentContainerStyle={{ backgroundColor: COLORS.BACKGROUND_COLORS.WHITE, flexGrow: 1 }}
        ItemSeparatorComponent={Separator}
        ListEmptyComponent={
          <EmptyState
            areFiltersApplied={areFiltersApplied}
            shouldDisableTransferPrescription={shouldDisableTransferPrescription}
          />
        }
        ListFooterComponent={isFetchingNextPage ? <Loader /> : null}
        ListHeaderComponent={
          <ListPage.Header.Root>
            <ListPage.Header.Content.Root>
              <Row
                wrap
                centerVertically
                spaceBetween
              >
                <ListPage.Header.Content.Title>Medications</ListPage.Header.Content.Title>
                {shouldDisableTransferPrescription ? null : (
                  <Row flexShrink={1}>
                    <AddMedicationButton />
                  </Row>
                )}
              </Row>

              <SmSpacing />

              {shouldShowTags ? (
                <Row
                  centerVertically
                  gap="XXS"
                  wrap
                >
                  <SortTag />
                  <FilterTypeTag />
                  {hasFamily ? <FamilyMemberFilterTag /> : null}
                </Row>
              ) : undefined}
            </ListPage.Header.Content.Root>
          </ListPage.Header.Root>
        }
        onEndReached={() => {
          if (hasNextPage && !isFetchingNextPage) fetchNextPage();
        }}
        renderItem={({ item }) => (
          <MedListItem
            medication={item}
            handleAddToCart={handleAddToCart}
          />
        )}
        refreshControl={
          <RefreshControl
            refreshing={refreshing}
            onRefresh={onRefresh}
          />
        }
      />
      {ViewCartComponent}
    </ListPage.Root>
  );
};
